import React, { useContext, useMemo, useRef } from 'react';
import { shallowEqual, useSelector } from 'umi';
import { isEqual } from 'lodash-es';
import moment from 'moment';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import { addLastDateRequestValue, getServiceFilterParams } from '@/pages/dashboard/utils/utils';
import { getDefaultUtcDateValue } from "@/pages/dashboard/utils/dateUtils";
import Service from '@/pages/dashboard/components/PageContainer/Service';
import { stringToFunction } from '@/pages/dashboard/utils/stringToJson';

const BreakDownContextCo = (props: any) => {
  const { children, config, changeData, ...resetProps } = props || {};

  const { api, byType, refreshKey = '', interval, haveLastDate, extraParams, hasByType, formatRules, formatData, extraTrackData = [], segmentLookbackDaysMap } = config;
  const dataSource = (props?.data as any) || {};
  const { metricsInfo, segmentValue } = dataSource || {};
  const timeZone = parseInt(dataSource?.time_zone || '', 10) || 0;
  const contextObj = useContext(FilterContext);
  const { filterVal, filterLoading } = contextObj || {};
  const gameCode = useSelector((state: any) => state?.dashboard?.currentGame?.game_code, shallowEqual);

  // 计算动态的 extraParams，使用深比较稳定化引用
  const resolvedExtraParamsRef = useRef<any>(undefined);
  const resolvedExtraParams = useMemo(() => {
    let nextValue;
    if (typeof extraParams === 'function') {
      nextValue = extraParams(dataSource);
    } else if (typeof formatData === 'string') {
      nextValue = stringToFunction(extraParams)(dataSource);
    } else {
      nextValue = extraParams;
    }
    // 深比较：内容相同时保持旧引用，避免触发下游 useEffect 重复请求
    if (isEqual(resolvedExtraParamsRef.current, nextValue)) {
      return resolvedExtraParamsRef.current;
    }
    resolvedExtraParamsRef.current = nextValue;
    return nextValue;
  }, [extraParams, dataSource]);

  const data = useMemo(() => ({
    game_code: gameCode,
    by_type: byType ? byType : 'os',
    metricsInfo,
    segmentValue,
    // 将 resolvedExtraParams 放入 data 中，确保请求时使用最新值
    _extraParams: resolvedExtraParams,
  }), [gameCode, byType, metricsInfo, segmentValue, resolvedExtraParams]);

  // 包装 formatData 以传递额外数据
  const wrappedFormatData = useMemo(() => {
    if (!formatData) return undefined;
    return (responseData: any) => {
      if (typeof extraParams === 'function') {
        return formatData(responseData, dataSource);
      }
      if (typeof formatData === 'string') {
        return stringToFunction(formatData)(responseData, dataSource);
      }
     
    };
  }, [formatData, dataSource]);

  // 合并 trackData
  const trackData = useMemo(() => {
    const baseTrackData = ['game_code', 'by_type'];
    if (segmentValue !== undefined) {
      baseTrackData.push('segmentValue');
    }
    // 添加 _extraParams 到 trackData 以监听变化
    baseTrackData.push('_extraParams');
    return [...baseTrackData, ...extraTrackData];
  }, [segmentValue, extraTrackData]);

  return (
    <Service
      api={api}
      data={data}
      refreshKey={refreshKey}
      interval={interval}
      dataKey={'data'}
      formatData={wrappedFormatData}
      formatRules={formatRules}
      beforeRequest={(reqData) => {
        const { filterVal: reqFilterVal, game_code, by_type, _extraParams, segmentValue: reqSegmentValue } = reqData;
        const defaultTimeRange = getDefaultUtcDateValue(reqFilterVal?.timeType, reqFilterVal?.rangeTime);
        const filterParams = getServiceFilterParams(reqFilterVal, defaultTimeRange);
        let params = {
          game_code,
          ...(hasByType ? { by_type } : {}),
          ...filterParams,
          ..._extraParams
        };
        const timeTypeKey = String(reqFilterVal?.timeType).toLowerCase();
        const lookbackConfig = reqSegmentValue && segmentLookbackDaysMap ? segmentLookbackDaysMap[reqSegmentValue] : undefined;
        const lookbackDays = lookbackConfig?.[timeTypeKey];
        if (lookbackDays && params.end_time) {
          params.start_time = moment(params.end_time).subtract(lookbackDays - 1, 'days').format('YYYY-MM-DD');
        }
        params = addLastDateRequestValue(params, haveLastDate, timeZone);
        return params;
      }}
      trackData={trackData}>
      {
        // 保证是jsx数组，而不是其他类型数组
        React.Children.map(children, (child: any) => React.cloneElement(
          child,
          {
            ...resetProps,
            filterVal,
            filterLoading,
            changeData,
            segmentValue,
          }
        ))
      }
    </Service>
  );
};
export default BreakDownContextCo;